/**
 * A name field with business-register autocomplete. Shared by the new-lead and
 * new-client modals.
 *
 * Typing queries `GET /admin/ariregister/autocomplete` (2 characters minimum,
 * debounced); picking a company then loads the full record (`getEntity`) and
 * prefills the parent form through `onPick` - name, registry code, VAT number,
 * email, phone, website and address.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Building2, Pencil } from "lucide-react";
import { Badge, Input, cn } from "@wa/ui";
import {
  autocomplete,
  getEntity,
  statusVariant,
  type ArAutocompleteItem,
} from "../../../lib/ariregister";
import { webHref } from "../../../lib/webUrl";

export interface ArPrefill {
  name: string;
  regCode: string;
  vatNumber: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
}

export function AriregisterNameInput({
  value,
  onChange,
  onPick,
  placeholder,
  required,
  enabled = true,
  hint,
  pill,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (p: ArPrefill) => void;
  placeholder?: string;
  required?: boolean;
  /** Turn autocomplete off (a private person has no registry entry). */
  enabled?: boolean;
  /** Help text under the field. */
  hint?: string;
  /** Wear the CRM's sunken pill instead of the shared bordered Input. */
  pill?: boolean;
}) {
  const [items, setItems] = useState<ArAutocompleteItem[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Loading the full record after a pick.
  const [fetching, setFetching] = useState(false);
  // Suppresses the query that would otherwise fire the moment a name is picked.
  const lastPicked = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setOpen(false);
      return;
    }
    const q = value.trim();
    // Too short, or the name that was just picked - do not query.
    if (q.length < 2 || (lastPicked.current !== null && value === lastPicked.current)) {
      setItems([]);
      setOpen(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      autocomplete(q)
        .then((r) => {
          if (!alive) return;
          setItems(r.items);
          setOpen(r.items.length > 0);
        })
        .catch(() => {
          if (!alive) return;
          setItems([]);
          setOpen(false);
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [value, enabled]);

  // A click outside closes the list.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function handleType(v: string) {
    // Typed by hand, so allow a fresh search.
    lastPicked.current = null;
    onChange(v);
  }

  async function pick(item: ArAutocompleteItem) {
    lastPicked.current = item.name;
    setOpen(false);
    setItems([]);
    setFetching(true);
    onChange(item.name);
    try {
      const e = await getEntity(item.regCode);
      lastPicked.current = e.name;
      onPick({
        name: e.name,
        regCode: e.regCode,
        vatNumber: e.vatNumber,
        email: e.email,
        phone: e.phone,
        // Normalised on the way in, not on the way out: this value stops being
        // a mirror of the register the moment it is saved onto a client, and
        // the register's raw form ("www.naide.ee", two addresses in one field)
        // would then be ours to render around forever.
        website: webHref(e.webUrl),
        address: e.addressFull,
      });
    } catch {
      // The detail call failed - at least keep the name, code and VAT number
      // that autocomplete already returned.
      onPick({
        name: item.name,
        regCode: item.regCode,
        vatNumber: item.vatNumber,
        email: null,
        phone: null,
        website: null,
        address: null,
      });
    } finally {
      setFetching(false);
    }
  }

  const busy = searching || fetching;
  const fieldProps = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleType(e.target.value),
    onFocus: () => items.length > 0 && setOpen(true),
    onKeyDown: (e: React.KeyboardEvent) => e.key === "Escape" && setOpen(false),
    placeholder,
    required,
    autoComplete: "off",
  };

  return (
    <div ref={boxRef} className="relative">
      {pill ? (
        <label className="crm-inp">
          <input {...fieldProps} />
          {busy ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <Pencil className="size-4 shrink-0 text-muted-foreground" />
          )}
        </label>
      ) : (
        <div className="relative">
          <Input {...fieldProps} />
          {busy ? (
            <Loader2 className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : null}
        </div>
      )}

      {open && items.length > 0 ? (
        <ul
          className={cn(
            "absolute z-50 max-h-64 w-full overflow-auto",
            pill
              ? "mt-2 rounded-3xl bg-[var(--crm-surface)] p-1.5 shadow-[var(--crm-sh-lg)]"
              : "mt-1 rounded-lg border border-border bg-background py-1 shadow-lg",
          )}
        >
          {items.map((it) => (
            <li key={it.regCode}>
              <button
                type="button"
                // Keep focus on the field until the click registers.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void pick(it)}
                className={cn(
                  "flex w-full items-start gap-2 text-left",
                  pill ? "rounded-2xl px-3.5 py-2.5 hover:bg-[var(--crm-sunk)]" : "px-3 py-1.5 hover:bg-accent",
                )}
              >
                <Building2 className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className={cn("block truncate", pill ? "crm-prow-n" : "text-sm font-medium")}>{it.name}</span>
                  <span className={cn("block", pill ? "crm-prow-s" : "text-xs text-muted-foreground")}>
                    {it.regCode}
                    {it.vatNumber ? ` · ${it.vatNumber}` : ""}
                  </span>
                </span>
                {it.status ? (
                  <Badge variant={statusVariant(it.status)} className="shrink-0 text-[10px]">
                    {it.status}
                  </Badge>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {hint ? <p className="mt-1.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
